import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Roles } from "~enums/roles";
import { useApi } from "~hooks/apiHooks/useApi";
import type { TransformedSeasonRoster } from "~pages/Roster/SeasonRosterListPage/types/transformedSeasonRoster.ts";
import { transformSeasonRoster } from "~pages/Roster/SeasonRosterListPage/utilities/transformSeasonRoster.ts";
import type { RoleResponse } from "~types/role";
import type { SeasonRosterResponse } from "~types/seasonRoster";
import { removeEmptyFields } from "~utils/removeEmptyFields.ts";

type Props = {
  customParams: Record<string, string>;
  entityId?: string;
};

const useFetchSeasonRoster = ({ customParams, entityId }: Props) => {
  const request = useApi();
  const { organizationId, sport, seasonId } = useParams();

  const params = new URLSearchParams(removeEmptyFields(customParams) as Record<string, string>);

  const captainParams = new URLSearchParams({
    limit: "1000",
    offset: "0",
    role: Roles.Captain,
    ...(entityId && { entityId }),
    ...(seasonId && { seasonId }),
  });

  const url = `${sport}/o/${organizationId}/seasons/${seasonId}/entities/${entityId}/roster?${params.toString()}`;
  const rolesUrl = `${sport}/o/${organizationId}/roles?${captainParams.toString()}`;

  return useQuery<Array<TransformedSeasonRoster>>({
    queryKey: ["seasonRoster", sport, organizationId, seasonId, entityId, params.toString()],
    queryFn: async () => {
      const [response, captainResponse] = await Promise.all([request<SeasonRosterResponse>(url), request<RoleResponse>(rolesUrl)]);
      return transformSeasonRoster(response, captainResponse);
    },
    enabled: !!entityId,
  });
};

export default useFetchSeasonRoster;
